import { BadgeCheck, HardHat, Sparkles, UserPlus } from 'lucide-react';
import { useEffect, useRef, type CSSProperties } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useAuth } from '../../app/auth';
import { Routes } from '../../app/routes';
import { useCatalog, useEmployeeRequests, useEmployees, useWorkspaces } from '../../data/queries';
import type { Employee, EmployeeModule, EmployeeRequest, Workspace } from '../../domain/models';
import { canRequestEmployee, isOpenRequest } from '../../domain/models';
import { useT } from '../../l10n/i18n';
import { timeAgo } from '../core/presentation';
import { EmployeeAvatar } from '../core/widgets/EmployeeAvatar';
import { ErrorView, Pill, Skeleton } from '../core/widgets/common';
import { autonomyBadge, employeeState, requestStatus, selectedWorkspace, teamHref, tint } from './team';
import './team.css';

// Workspaces and the employees working in each, as the mobile app's team screen. The chosen
// workspace lives in the address (`?workspace=slug`), so a link or the back button keeps it.

export function TeamPage() {
  const t = useT();
  const { session } = useAuth();
  const workspacesQ = useWorkspaces();
  const employeesQ = useEmployees();
  const requests = (useEmployeeRequests().data ?? []).filter(isOpenRequest);
  const [params] = useSearchParams();

  const workspaces = workspacesQ.data;
  const selected = selectedWorkspace(workspaces, params.get('workspace'));
  const employees = (employeesQ.data ?? []).filter((e) => e.workspaceId === selected?.id);
  const retry = () => {
    void workspacesQ.refetch();
    void employeesQ.refetch();
  };

  const header = (
    <header className="page-header team-header">
      <div>
        <h1 className="display-small">{t('navTeam')}</h1>
        <p className="body-medium muted">{t('teamSubtitle')}</p>
      </div>
      {canRequestEmployee(session) && (
        <Link to={Routes.hire(selected?.slug)} className="btn btn-filled team-hire">
          <UserPlus size={18} aria-hidden />
          {t('teamHire')}
        </Link>
      )}
    </header>
  );

  if (!workspaces && workspacesQ.isError) {
    return (
      <div className="page">
        {header}
        <ErrorView error={workspacesQ.error} onRetry={retry} />
      </div>
    );
  }

  return (
    <div className="page">
      {header}
      {requests.length > 0 && <Requests requests={requests} workspaces={workspaces ?? []} />}
      <div className="ws-strip">
        {workspaces
          ? workspaces.map((w) => <WorkspaceTile key={w.id} workspace={w} selected={w.id === selected?.id} />)
          : [0, 1].map((i) => <Skeleton key={i} width={160} height={92} radius={22} />)}
      </div>
      {selected?.description && <p className="body-medium muted team-description">{selected.description}</p>}
      {workspaces && !employeesQ.data && employeesQ.isError ? (
        <ErrorView error={employeesQ.error} onRetry={retry} />
      ) : (
        <ul className="team-grid">
          {workspaces && employeesQ.data
            ? employees.map((e) => (
                <li key={e.id}>
                  <EmployeeCard employee={e} />
                </li>
              ))
            : [0, 1].map((i) => (
                <li key={i}>
                  <Skeleton height={196} radius={22} />
                </li>
              ))}
        </ul>
      )}
    </div>
  );
}

/** Employees asked for and not yet working: what the Talkos team is setting up. */
function Requests({ requests, workspaces }: { requests: EmployeeRequest[]; workspaces: Workspace[] }) {
  const t = useT();
  const modules = useCatalog(requests.some((r) => r.role !== undefined)).data;
  return (
    <section className="team-requests" aria-labelledby="team-requests-title">
      <h2 id="team-requests-title" className="overline">
        {t('teamRequests')}
      </h2>
      <ul className="card team-request-list">
        {requests.map((r) => (
          <li key={r.id}>
            <RequestRow request={r} module={modules?.find((m) => m.role === r.role)} workspaces={workspaces} />
          </li>
        ))}
      </ul>
    </section>
  );
}

function RequestRow({ request: r, module, workspaces }: { request: EmployeeRequest; module?: EmployeeModule; workspaces: Workspace[] }) {
  const t = useT();
  const status = requestStatus(t, r.status);
  const business = workspaces.find((w) => w.id === r.workspaceId)?.name ?? r.workspaceName;
  return (
    <div className="team-request">
      {module ? (
        <EmployeeAvatar avatar={module.avatar} color={module.color} size={40} />
      ) : (
        <span className="team-request-icon" aria-hidden>
          <Sparkles size={20} />
        </span>
      )}
      <span className="team-request-text">
        <span className="title-small">{module ? `${module.name} · ${r.title}` : r.title}</span>
        <span className="body-small">
          {business && `${business} · `}
          {t('requestMeta', { name: r.requestedBy, when: timeAgo(t, r.createdAt) })}
        </span>
      </span>
      <Pill label={status.label} tone={status.tone} dot={status.working ? 'var(--accent)' : undefined} pulse={status.working} />
    </div>
  );
}

function WorkspaceTile({ workspace: w, selected }: { workspace: Workspace; selected: boolean }) {
  const t = useT();
  const ref = useRef<HTMLAnchorElement>(null);
  // On a phone the projects scroll sideways: keep the chosen one in sight.
  useEffect(() => {
    if (selected) ref.current?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
  }, [selected]);
  return (
    <Link
      ref={ref}
      to={teamHref(w.slug)}
      replace
      preventScrollReset
      className={selected ? 'ws-tile selected' : 'ws-tile'}
      aria-current={selected ? 'page' : undefined}
      style={{ '--ws': w.color } as CSSProperties}
    >
      <span className="ws-top">
        <span className="ws-dot" />
        {w.openCount > 0 && (
          <span className="ws-count" aria-hidden>
            {w.openCount}
          </span>
        )}
      </span>
      <span className="ws-name">{w.name}</span>
      <span className="ws-sub ellipsis">{w.openCount > 0 ? t('teamOpen', { count: w.openCount }) : t('teamIdle')}</span>
    </Link>
  );
}

function EmployeeCard({ employee: e }: { employee: Employee }) {
  const t = useT();
  const state = employeeState(t, e);
  const autonomy = autonomyBadge(t, e);
  return (
    <Link to={Routes.employee(e.id)} className="team-card" style={{ '--emp': e.color } as CSSProperties}>
      <span className="team-card-top">
        <EmployeeAvatar avatar={e.avatar} color={e.color} size={64} animate />
        {e.openCount > 0 && (
          <span className="team-count" aria-hidden>
            {e.openCount}
          </span>
        )}
      </span>
      <span className="team-card-body">
        {(e.takesTasks || autonomy) && (
          <span className="team-pills">
            {e.takesTasks && (
              <Pill label={t('teamTakesTasks')} icon={HardHat} color={e.color} background={tint(e.color)} dense />
            )}
            {autonomy && <Pill label={autonomy} icon={BadgeCheck} color={e.color} background={tint(e.color)} dense />}
          </span>
        )}
        <span className="title-large team-name">{e.name}</span>
        <span className="body-small ellipsis">{e.title}</span>
        {e.description && <span className="body-small team-about">{e.description}</span>}
        <span className="label-small team-state">
          <span className={state.pulse ? 'dot pulse' : 'dot'} style={{ background: state.dot }} />
          <span className="ellipsis">{state.label}</span>
        </span>
      </span>
    </Link>
  );
}
